/**
 * Catálogo de preços da tela de licença armazenado em `pay_types.license_catalog`.
 * O card "Plano atual" na loja **não** vem daqui — vem de `clientes.licenca_loja` (jsonb), controlado pelo front.
 */

export const LICENSE_CATALOG_PAY_TYPES_KEY = "license_catalog";

export interface LicenseTierEdit {
  tierLabel: string;
  daysLabel: string;
  priceDisplay: string;
  periodLabel: string;
  /** Ex.: "Economia R$ 50" (Suprema) */
  savingsNote?: string;
  /** Dias cobrados neste pacote (checkout PIX envia ao webhook). Omitir/zero desabilita pagamento PIX no card. */
  billingDays?: number;
  /** Valor esperado para Santander/n8n, em centavos. Omitir se o webhook calcular apenas por dias (não recomendado para preços por módulo). */
  valorCentavos?: number;
}

export interface LicenseModuleBlock {
  categoryTitle: string;
  categorySubtitle: string;
  monthly: LicenseTierEdit;
  quarterly: LicenseTierEdit;
  annual: LicenseTierEdit;
}

export interface DeliveryPackCatalog {
  /** Título exibido no card (painel cliente + editável pela equipe) */
  title: string;
  subtitle: string;
  /** Opcional — substitui o texto principal do aviso âmbar; vazio mantém texto i18n padrão */
  exclusionNotice?: string;
  monthly: LicenseTierEdit;
  semiannual: LicenseTierEdit;
  annual: LicenseTierEdit;
  annualExtraNote?: string;
}

export interface LicenseCatalogState {
  modules: {
    atendimento: LicenseModuleBlock;
    motoboy: LicenseModuleBlock;
    gerente: LicenseModuleBlock;
    agendamento: LicenseModuleBlock;
  };
  suprema: {
    title: string;
    subtitleSuffix: string;
    monthly: LicenseTierEdit;
    quarterly: LicenseTierEdit;
    annual: LicenseTierEdit;
    /** Texto extra no anual (ex.: popular) */
    annualExtraNote?: string;
  };
  /** Pacote gestão sem módulo de atendimento (card delivery / operação) */
  deliveryPack: DeliveryPackCatalog;
}

type TierBilling = { billingDays: number; valorCentavos: number };

function tier(
  tierLabel: string,
  daysLabel: string,
  priceDisplay: string,
  periodLabel: string,
  savingsNote?: string,
  billing?: TierBilling
): LicenseTierEdit {
  return {
    tierLabel,
    daysLabel,
    priceDisplay,
    periodLabel,
    savingsNote,
    ...(billing ?? {}),
  };
}

function moduleBlock(
  categoryTitle: string,
  categorySubtitle: string,
  prices: readonly [string, string, string],
  billing: readonly [TierBilling, TierBilling, TierBilling]
): LicenseModuleBlock {
  const [m, q, a] = prices;
  return {
    categoryTitle,
    categorySubtitle,
    monthly: tier("Mensal", `${billing[0].billingDays} dias`, m, "por mês", undefined, billing[0]),
    quarterly: tier("Trimestral", `${billing[1].billingDays} dias`, q, "por trimestre", undefined, billing[1]),
    annual: tier("Anual", `${billing[2].billingDays} dias`, a, "por ano", undefined, billing[2]),
  };
}

/** Defaults alinhados à oferta inicial; valores editáveis no painel DelivAI Licenças. */
export const defaultLicenseCatalog: LicenseCatalogState = {
  modules: {
    atendimento: moduleBlock(
      "Atendimento (WhatsApp)",
      "Gestão de conversas e atendimento",
      ["R$ 120", "R$ 300", "R$ 1.200"],
      [
        { billingDays: 30, valorCentavos: 12000 },
        { billingDays: 90, valorCentavos: 30000 },
        { billingDays: 365, valorCentavos: 120000 },
      ]
    ),
    motoboy: moduleBlock(
      "Motoboy",
      "Gestão de entregadores",
      ["R$ 80", "R$ 200", "R$ 800"],
      [
        { billingDays: 30, valorCentavos: 8000 },
        { billingDays: 90, valorCentavos: 20000 },
        { billingDays: 365, valorCentavos: 80000 },
      ]
    ),
    gerente: moduleBlock(
      "Gerente",
      "Gestão administrativa completa",
      ["R$ 150", "R$ 400", "R$ 1.500"],
      [
        { billingDays: 30, valorCentavos: 15000 },
        { billingDays: 90, valorCentavos: 40000 },
        { billingDays: 365, valorCentavos: 150000 },
      ]
    ),
    agendamento: moduleBlock(
      "Agendamentos",
      "Agenda e integração de agendamento",
      ["R$ 90", "R$ 220", "R$ 900"],
      [
        { billingDays: 30, valorCentavos: 9000 },
        { billingDays: 90, valorCentavos: 22000 },
        { billingDays: 365, valorCentavos: 90000 },
      ]
    ),
  },
  suprema: {
    title: "SUPREMA",
    subtitleSuffix: "- Todos os módulos",
    monthly: tier("Mensal", "30 dias", "R$ 300", "por mês", "Economia R$ 50", {
      billingDays: 30,
      valorCentavos: 30000,
    }),
    quarterly: tier("Trimestral", "90 dias", "R$ 800", "por trimestre", "Economia R$ 100", {
      billingDays: 90,
      valorCentavos: 80000,
    }),
    annual: tier("Anual", "365 dias", "R$ 3.000", "por ano", "Economia R$ 500", {
      billingDays: 365,
      valorCentavos: 300000,
    }),
    annualExtraNote: "⭐ Mais popular",
  },
  deliveryPack: {
    title: "Plano Operação Completa",
    subtitle:
      "Pedidos, financeiro, colaboradores, motoboys e cadastros — opção econômica para quem não usa o fluxo de atendimento (conversas no WhatsApp) no painel.",
    exclusionNotice: undefined,
    monthly: tier(
      "Mensal",
      "30 dias",
      "R$ 199,99",
      "por mês",
      undefined,
      { billingDays: 30, valorCentavos: 19999 }
    ),
    semiannual: tier(
      "Semestral",
      "180 dias",
      "R$ 979,99",
      "por semestre",
      "180 dias · economia vs. mensal",
      { billingDays: 180, valorCentavos: 97999 }
    ),
    annual: tier(
      "Anual",
      "365 dias",
      "R$ 1.799,99",
      "por ano",
      "365 dias · menor custo por dia",
      { billingDays: 365, valorCentavos: 179999 }
    ),
    annualExtraNote: "⭐ Mais vantagem",
  },
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function nonemptyTrimmedString(v: unknown): v is string {
  return typeof v === "string" && v.trim() !== "";
}

function mergeTier(base: LicenseTierEdit, patch: unknown): LicenseTierEdit {
  if (!isPlainObject(patch)) return base;
  const billingDaysPatch = patch.billingDays;
  const valorCentavosPatch = patch.valorCentavos;
  return {
    tierLabel: nonemptyTrimmedString(patch.tierLabel) ? patch.tierLabel.trim() : base.tierLabel,
    daysLabel: nonemptyTrimmedString(patch.daysLabel) ? patch.daysLabel.trim() : base.daysLabel,
    priceDisplay: nonemptyTrimmedString(patch.priceDisplay) ? patch.priceDisplay.trim() : base.priceDisplay,
    periodLabel: nonemptyTrimmedString(patch.periodLabel) ? patch.periodLabel.trim() : base.periodLabel,
    savingsNote: nonemptyTrimmedString(patch.savingsNote) ? patch.savingsNote.trim() : base.savingsNote,
    billingDays:
      typeof billingDaysPatch === "number" &&
      Number.isFinite(billingDaysPatch) &&
      billingDaysPatch >= 1
        ? Math.round(billingDaysPatch)
        : base.billingDays,
    valorCentavos:
      typeof valorCentavosPatch === "number" &&
      Number.isFinite(valorCentavosPatch) &&
      valorCentavosPatch >= 0
        ? Math.round(valorCentavosPatch)
        : base.valorCentavos,
  };
}

function mergeModule(base: LicenseModuleBlock, patch: unknown): LicenseModuleBlock {
  if (!isPlainObject(patch)) return base;
  return {
    categoryTitle: nonemptyTrimmedString(patch.categoryTitle)
      ? patch.categoryTitle.trim()
      : base.categoryTitle,
    categorySubtitle: nonemptyTrimmedString(patch.categorySubtitle)
      ? patch.categorySubtitle.trim()
      : base.categorySubtitle,
    monthly: mergeTier(base.monthly, patch.monthly),
    quarterly: mergeTier(base.quarterly, patch.quarterly),
    annual: mergeTier(base.annual, patch.annual),
  };
}

function mergeDeliveryPack(base: DeliveryPackCatalog, patch: unknown): DeliveryPackCatalog {
  if (!isPlainObject(patch)) return base;
  const rec = patch as Record<string, unknown>;
  const semiannualRaw = rec.semiannual ?? rec.semiAnnual ?? rec.semestral;

  return {
    title: nonemptyTrimmedString(rec.title) ? String(rec.title).trim() : base.title,
    subtitle: nonemptyTrimmedString(rec.subtitle) ? String(rec.subtitle).trim() : base.subtitle,
    exclusionNotice:
      nonemptyTrimmedString(rec.exclusionNotice)
        ? String(rec.exclusionNotice).trim()
        : nonemptyTrimmedString(rec.exclusion_notice)
          ? String(rec.exclusion_notice).trim()
          : base.exclusionNotice,
    monthly: mergeTier(base.monthly, rec.monthly),
    semiannual: mergeTier(base.semiannual, semiannualRaw),
    annual: mergeTier(base.annual, rec.annual),
    annualExtraNote: nonemptyTrimmedString(rec.annualExtraNote)
      ? String(rec.annualExtraNote).trim()
      : base.annualExtraNote,
  };
}

export function mergeLicenseCatalogFromPayTypes(payTypes: unknown): LicenseCatalogState {
  const out: LicenseCatalogState = JSON.parse(JSON.stringify(defaultLicenseCatalog));
  if (!isPlainObject(payTypes)) return out;
  const raw = payTypes[LICENSE_CATALOG_PAY_TYPES_KEY];
  if (!isPlainObject(raw)) return out;

  const mods = raw.modules;
  if (isPlainObject(mods)) {
    (["atendimento", "motoboy", "gerente", "agendamento"] as const).forEach((k) => {
      out.modules[k] = mergeModule(out.modules[k], mods[k]);
    });
  }

  if (isPlainObject(raw.suprema)) {
    const s = raw.suprema;
    out.suprema = {
      title: nonemptyTrimmedString(s.title) ? String(s.title).trim() : out.suprema.title,
      subtitleSuffix: nonemptyTrimmedString(s.subtitleSuffix)
        ? String(s.subtitleSuffix).trim()
        : out.suprema.subtitleSuffix,
      monthly: mergeTier(out.suprema.monthly, s.monthly),
      quarterly: mergeTier(out.suprema.quarterly, s.quarterly),
      annual: mergeTier(out.suprema.annual, s.annual),
      annualExtraNote: nonemptyTrimmedString(s.annualExtraNote)
        ? String(s.annualExtraNote).trim()
        : out.suprema.annualExtraNote,
    };
  }

  if (isPlainObject(raw.deliveryPack)) {
    out.deliveryPack = mergeDeliveryPack(out.deliveryPack, raw.deliveryPack);
  }

  return out;
}

export function applyLicenseCatalogToPayTypes(
  payTypes: Record<string, unknown>,
  catalog: LicenseCatalogState
): Record<string, unknown> {
  return {
    ...payTypes,
    [LICENSE_CATALOG_PAY_TYPES_KEY]: JSON.parse(JSON.stringify(catalog)),
  };
}

/** Condições mínimas para exibir fluxo PIX a partir do card (ajustável no painel). */
export function tierAllowsPixCheckout(tier: LicenseTierEdit): boolean {
  const d = tier.billingDays;
  const c = tier.valorCentavos;
  return (
    typeof d === "number" &&
    Number.isFinite(d) &&
    d >= 1 &&
    typeof c === "number" &&
    Number.isFinite(c) &&
    c >= 50
  );
}
